import React, {useState, useCallback} from 'react';
import {useNavigate, useLocation} from "react-router-dom";
import Box from '@mui/material/Box';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import {useAuth} from "../../_auth/useAuth";
import {useTheme} from "@mui/material/styles";
import {confirmSignupFields} from "../../_appData";
import { AuthConfirmSignupOptions } from "../../_auth/types";
import {FormPaper} from "./Signup.styles";
import {Form,
    ButtonBar,
    DaButton,
    DaTextField,
    MessagePanel,
    IMessagePanelProps,
} from "../_shared";
import {ISignupConfirm} from "../../_api";

const signupConfirmSchema: yup.SchemaOf<ISignupConfirm> = yup.object({
    first_name: yup.string().label(confirmSignupFields.firstName.label).max(150).required(),
    last_name: yup.string().label(confirmSignupFields.lastName.label).max(150).required(),
    email: yup.string().label(confirmSignupFields.email.label).max(254).required(),
    phone_number: yup.string().label(confirmSignupFields.phone.label).max(25).optional(),
    sub: yup.string().label(confirmSignupFields.userName.label).max(254).required(),
    code: yup.string().label(confirmSignupFields.confirmationCode.label).required(),
    group: yup.string().label("Group").optional(),
});

export const SignupConfirmView: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { signupConfirm } = useAuth();
    const [msgPanelProps, setMsgPanelProps] = useState<IMessagePanelProps>({message: ""});
    const [progressTitle, setProgressTitle] = useState<string>("Processing Signup Info...");
    const [isShowError, setIsShowError] = useState<boolean>(false);
    const [showProgress, setShowProgress] = useState<boolean>(false);

    const formFieldSize = "medium";
    const formFieldVariant = "standard";
    const { email, first_name, last_name, phone_number, sub, group} = state as AuthConfirmSignupOptions; // Read values passed on state

    // defining the initial state for the form
    const initialValues = {
        first_name: first_name,
        last_name: last_name,
        email: email,
        phone_number: phone_number,
        sub: sub,
        code: '',
        group: group
        // officer_title: officer_title,
        // fax: fax,
        // federal_tax_id: federal_tax_id,
    };

    // get event handlers from our react=hook-form
    const { control, handleSubmit, reset, formState: { errors } } = useForm<ISignupConfirm>({
        defaultValues: initialValues,
        resolver: yupResolver(signupConfirmSchema)
    });

    const showError = ({title, message, type="error"}: IMessagePanelProps) => {
        const msgProps: IMessagePanelProps = {
            message: message,
            title: title,
            type: type
        }
        setMsgPanelProps(msgProps);
        setIsShowError(true);
    }

    // a submit function that will execute upon form submission
    const onSubmit: SubmitHandler<ISignupConfirm> = (data) => {
        submitSignupConfirm(data);
    };

    const submitSignupConfirm = useCallback(async (options: AuthConfirmSignupOptions): Promise<void> => {
        setProgressTitle("Confirming Signup Info...");
        setShowProgress(true);

        let apiPromise = undefined;
        apiPromise = signupConfirm(options);
        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.state === "success"){
                navigate("/");
            } else {
                showError({
                    title: "Unable to Confirm Signup Info",
                    message: "An error occurred while confirming Signup data. " +
                        "Verify that you have provided all required data and " +
                        "retry your last action."
                });
            }
        })
        .catch(() => {
            showError({
                title: "Confirm Signup API Save Error",
                message: "An error occurred while confirming Signup data. " +
                    "There may have been a network error.",
            });
        })
        .finally(() => {
            setShowProgress(false);
        });
    }, [signupConfirm, navigate]);


    return (
        <>
            <FormPaper elevation={4}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <AccountCircleIcon
                        sx={{
                            fontSize: 68,
                            color: theme.palette.grey["500"],
                            mb: 2
                        }}
                    />
                    <Typography variant="h4">Confirm Sign Up</Typography>
                </Box>
                <Grid container>
                    {isShowError ? (
                        <Grid item xs={12} justifyContent={"start"}>
                            <MessagePanel
                                {...msgPanelProps}
                            />
                        </Grid>
                    ) : null}
                    <Grid container item xs={12}>
                        <Form
                            autoComplete="off"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <Grid container spacing={theme.spacing(1)}>
                                <Grid item xs={12} md={12}>
                                    <DaTextField
                                        control={control}
                                        name={confirmSignupFields.confirmationCode.name}
                                        label={confirmSignupFields.confirmationCode.label}
                                        required={confirmSignupFields.confirmationCode.required}
                                        variant={formFieldVariant}
                                        size={formFieldSize}
                                        errors={errors}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={theme.spacing(1)}>
                                <Grid item xs={12} md={12}>
                                    <ButtonBar buttonAlignment="alignRight">
                                        <DaButton buttonStyle="primary" type="submit">
                                            Confirm Sign Up
                                        </DaButton>
                                    </ButtonBar>
                                </Grid>
                            </Grid>
                        </Form>
                    </Grid>
                </Grid>
            </FormPaper>
        </>
    );
}
